import { useEffect, useRef, useState } from "react";
import { FileText, Loader2, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useFT } from "./Formation";

const TYPES = ["passport", "photo", "address_proof", "visa", "other"];

export function DossierDocuments({ token }) {
  const t = useFT();
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState("passport");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef();
  const load = () => api.get(`/public/formation/dossier/${token}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [token]); // eslint-disable-line
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    const fd = new FormData(); fd.append("file", file); fd.append("type", type);
    try { await api.post(`/public/formation/dossier/${token}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); }
    catch (e) { setError(e?.response?.data?.detail || t("fError")); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const del = async (id) => { await api.delete(`/public/formation/dossier/${token}/documents/${id}`); load(); };
  return (
    <section data-testid="dossier-documents" className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
      <h2 className="font-display text-xl font-bold">{t("docsTitle")}</h2>
      <p className="mt-1 text-sm text-white/55">{t("docsText")}</p>
      <div className="mt-5 flex flex-wrap gap-2">{TYPES.map((k) => <button key={k} type="button" data-testid={`doc-type-${k}`} onClick={() => setType(k)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${type === k ? "border-[#D4AF37] bg-[#D4AF37] text-[#1A1400]" : "border-white/15 text-white/70"}`}>{t(`doc_${k}`)}</button>)}</div>
      <label data-testid="doc-dropzone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }} className="mt-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/15 p-8 text-center text-sm text-white/55 transition-colors hover:border-[#00C462]/60">
        {busy ? <Loader2 className="h-6 w-6 animate-spin text-[#00C462]" /> : <Upload className="h-6 w-6 text-[#00C462]" />}
        <span>{t("docsDrop")}</span><span className="text-xs text-white/35">{t("docsFormats")}</span>
        <input ref={ref} data-testid="doc-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" className="hidden" onChange={(e) => upload(e.target.files[0])} disabled={busy} />
      </label>
      {error && <p data-testid="doc-error" className="mt-2 text-sm text-[#FF7B8A]">{error}</p>}
      <ul className="mt-5 space-y-2">
        {docs.length === 0 && <li className="text-sm text-white/35">{t("docsEmpty")}</li>}
        {docs.map((d) => <li key={d.id} data-testid={`doc-item-${d.id}`} className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/20 px-4 py-2.5 text-sm">
          <FileText className="h-4 w-4 shrink-0 text-[#D4AF37]" /><span className="rounded bg-white/10 px-2 py-0.5 text-[11px] uppercase tracking-wider text-white/60">{t(`doc_${d.type}`)}</span>
          <span className="flex-1 truncate">{d.filename}</span><span className="text-xs text-white/35">{Math.max(1, Math.round(d.size / 1024))} KB</span>
          <button data-testid={`doc-delete-${d.id}`} onClick={() => del(d.id)} className="text-white/40 hover:text-[#FF7B8A]"><Trash2 className="h-4 w-4" /></button>
        </li>)}
      </ul>
    </section>
  );
}
